import currency from 'currency.js';
import { createSelector } from 'reselect';
import { omit } from 'lodash';
import { round, divide } from 'mathjs';
import { ColumnLabel, ExchangeRateLabel } from 'constants/labels';
import { ColumnTypes } from 'constants/grid';
import { subsidiariesSelector } from 'selectors/consolidations';
import { settingsSelector } from 'selectors/settings';
import {
  currencySum,
  currencyValueFormatter,
  numberParser,
  kpiValueFormatter,
} from 'utils/generic';
import { currencyComparator } from 'utils/gridSortingComparators';

export function convertWithExchangeRate(amount, exchangeRate) {
  const currencyAmountValue = currency(amount).value;

  const result = divide(currencyAmountValue, exchangeRate);

  const rounded = round(result, 2);

  return currency(rounded);
}

export function calculateNet(data, periodId) {
  return currency(data[`unadjusted-${periodId}`]).add(
    data[`adjusted-${periodId}`]
  );
}

export function calculateNetEr(data, periodId) {
  const existingNetEr = data[`net-er-${periodId}`];

  if (existingNetEr) {
    return existingNetEr;
  }

  const net = calculateNet(data, periodId);
  const exchangeRate = data[`er-${periodId}`];

  if (!exchangeRate) {
    return net;
  }

  return convertWithExchangeRate(net, exchangeRate);
}

export function calculateNetErWithAdjusted(data, periodId) {
  const existingNetEr = data[`net-er-${periodId}`]?.intValue !== 0;

  if (!existingNetEr) {
    return convertWithExchangeRate(
      data[`adjusted-${periodId}`],
      data[`er-${periodId}`]
    );
  }

  return currency(data[`net-er-${periodId}`]).add(data[`adjusted-${periodId}`]);
}

export function calculateVar(data, periodId1, periodId2) {
  return currency(calculateNetEr(data, periodId1)).subtract(
    calculateNetEr(data, periodId2)
  );
}

export const periodIdsSelector = (state) => state.accounts.periodIds;
export const periodsByIdSelector = (state) => state.accounts.periodsById;

export const periodsSelector = createSelector(
  periodIdsSelector,
  periodsByIdSelector,
  (periodIds, periodsById) => {
    return periodIds.map((id) => ({
      id,
      ...periodsById[id],
    }));
  }
);

export const periodColumnsExchangeRatesSelector = createSelector(
  periodsSelector,
  (state, props) => props || {},
  (periods) => {
    const nameCol = {
      field: 'name',
      colId: 'name',
      headerName: ExchangeRateLabel.name,
      editable: true,
    };

    const periodCols = periods.map((period) => ({
      field: `er-${period.id}`,
      colId: `er-${period.id}`,
      headerName: period.name,
      type: ColumnTypes.numeric,
      editable: true,
      valueParser: numberParser,
    }));

    return [nameCol, ...periodCols];
  }
);

export const periodColumnsSelector = createSelector(
  periodsSelector,
  subsidiariesSelector,
  settingsSelector,
  (state, props) => props || {},
  (
    periods,
    subsidiaries,
    settings,
    {
      isGroups,
      useGroupsPrecision,
      editable,
      hasMostImportantCols,
      hasMostImportantPeriods,
    }
  ) => {
    const customCurrencyValueFormatter = currencyValueFormatter(
      settings,
      useGroupsPrecision
    );
    const mostImportantPeriodsIndexes = [0, 1];
    const getIsImportantPeriod = (periodIndex) =>
      hasMostImportantPeriods
        ? mostImportantPeriodsIndexes.includes(periodIndex)
        : true;

    const cols = periods.map((period, periodIndex) => {
      const isImportantPeriod = getIsImportantPeriod(periodIndex);

      return {
        groupId: period.id,
        headerName: period.name,
        marryChildren: true,
        children: [
          {
            field: `unadjusted-${period.id}`,
            colId: `unadjusted-${period.id}`,
            headerName: ColumnLabel.unadjusted,
            hide: hasMostImportantCols || !isImportantPeriod,
            editable: editable ? (params) => !params.node.rowPinned : false,
            type: ColumnTypes.numeric,
            aggFunc: currencySum,
            valueParser: numberParser,
            valueFormatter: customCurrencyValueFormatter,
            comparator: currencyComparator,
          },
          {
            field: `adjusted-${period.id}`,
            colId: `adjusted-${period.id}`,
            headerName: ColumnLabel.adjusted,
            hide: hasMostImportantCols || !isImportantPeriod,
            type: ColumnTypes.numeric,
            valueFormatter: customCurrencyValueFormatter,
            comparator: currencyComparator,
          },
          {
            field: `net-${period.id}`,
            colId: `net-${period.id}`,
            headerName: ColumnLabel.net,
            hide: hasMostImportantCols || !isImportantPeriod,
            type: ColumnTypes.numeric,
            valueGetter: ({ data }) => calculateNet(data, period.id),
            valueFormatter: customCurrencyValueFormatter,
            comparator: currencyComparator,
          },
          {
            field: `net-er-${period.id}`,
            colId: `net-er-${period.id}`,
            headerName: ColumnLabel.netEr,
            hide: !isImportantPeriod,
            type: ColumnTypes.numeric,
            valueFormatter: customCurrencyValueFormatter,
            comparator: currencyComparator,
          },
        ],
      };
    });

    // difference columns
    periods.forEach((period, periodIndex) => {
      if (periodIndex > 0) {
        const period1 = periods[periodIndex - 1];
        const period2 = period;

        const shouldHideColumn = periodIndex > 1;
        const isImportantPeriod = getIsImportantPeriod(periodIndex);

        // 2 * periodIndex -> for every period (excluding first period), one var column is added
        cols.splice(2 * periodIndex, 0, {
          groupId: 'difference',
          headerName: `Difference (${period1.name} - ${period2.name})`,
          marryChildren: true,
          children: [
            {
              field: 'diff-abs',
              colId: 'diff-abs',
              headerName: 'Diff (abs.)',
              hide:
                shouldHideColumn || hasMostImportantCols || !isImportantPeriod,
              type: ColumnTypes.numeric,
              valueGetter: ({ data }) =>
                calculateVar(data, period1.id, period2.id),
              valueFormatter: customCurrencyValueFormatter,
              comparator: currencyComparator,
            },
            {
              field: 'diff-rel',
              colId: 'diff-rel',
              headerName: 'Diff (%)',
              hide:
                shouldHideColumn || hasMostImportantCols || !isImportantPeriod,
              type: ColumnTypes.numeric,
              valueGetter: ({ data }) => {
                const netEr2 = calculateNetEr(data, period2.id);

                if (!netEr2.value) {
                  // to avoid dividing by 0
                  return currency(0);
                }

                return calculateVar(data, period1.id, period2.id)
                  .divide(netEr2)
                  .multiply(100);
              },
              valueFormatter: customCurrencyValueFormatter,
              comparator: currencyComparator,
            },
          ],
        });
      }
    });

    // In case of Groups, add Consolidation columns
    if (isGroups && subsidiaries.length) {
      // Add columns for the first period
      const periodIndex = 0;
      const periodId = periods[periodIndex].id;
      const subsTotalGetter = (params) =>
        currencySum(
          subsidiaries.map(
            (subsidiary) => params.data[`sub-${subsidiary.id}-${periodId}`]
          )
        );
      subsidiaries.forEach((subsidiary) => {
        cols[0].children.push({
          field: `sub-${subsidiary.id}-${periodId}`,
          colId: `sub-${subsidiary.id}-${periodId}`,
          headerName: `${subsidiary.name} Bal.`,
          hide: hasMostImportantCols,
          type: ColumnTypes.numeric,
          valueFormatter: customCurrencyValueFormatter,
          comparator: currencyComparator,
        });
      });

      cols[0].children.push({
        field: `subs-total-${periodId}`,
        colId: `subs-total-${periodId}`,
        headerName: 'Subs Total',
        hide: hasMostImportantCols,
        type: ColumnTypes.numeric,
        valueGetter: subsTotalGetter,
        valueFormatter: customCurrencyValueFormatter,
        comparator: currencyComparator,
      });
      cols[0].children.push({
        field: `cje-${periodId}`,
        colId: `cje-${periodId}`,
        headerName: 'EJ',
        hide: hasMostImportantCols,
        type: ColumnTypes.numeric,
        valueFormatter: customCurrencyValueFormatter,
        comparator: currencyComparator,
      });
      cols[0].children.push({
        field: `grand-total-${periodId}`,
        colId: `grand-total-${periodId}`,
        headerName: 'Grand Total',
        type: ColumnTypes.numeric,
        valueGetter: (params) => {
          const hasNetEr = !!params.data[`net-er-${periodId}`];

          if (hasNetEr) {
            return subsTotalGetter(params)
              .add(params.data[`net-er-${periodId}`])
              .add(params.data[`cje-${periodId}`]);
          }

          return subsTotalGetter(params)
            .add(params.data[`unadjusted-${periodId}`])
            .add(params.data[`adjusted-${periodId}`])
            .add(params.data[`cje-${periodId}`]);
        },
        valueFormatter: customCurrencyValueFormatter,
        comparator: currencyComparator,
      });

      // Add consolidation columns for other periods
      // slice returns empty array if there is no second period
      periods.slice(1).forEach((period, periodIndex) => {
        const currentPeriodId = period.id;
        const periodGroupCol = cols.find(
          (col) => col.groupId === currentPeriodId
        );
        const isImportantPeriod = getIsImportantPeriod(periodIndex);

        periodGroupCol.children.push({
          field: `constrial-${currentPeriodId}`,
          colId: `constrial-${currentPeriodId}`,
          headerName: 'Consolidated Trial Balance',
          hide: hasMostImportantCols || !isImportantPeriod,
          type: ColumnTypes.numeric,
          valueGetter: consolidationTrialBalanceGetter(
            subsidiaries,
            currentPeriodId
          ),
          valueFormatter: customCurrencyValueFormatter,
          comparator: currencyComparator,
        });

        periodGroupCol.children.push({
          field: `consjournal-${currentPeriodId}`,
          colId: `consjournal-${currentPeriodId}`,
          headerName: 'EJ',
          hide: hasMostImportantCols || !isImportantPeriod,
          type: ColumnTypes.numeric,
          valueGetter: consolidationJournalEntriesGetter(currentPeriodId),
          valueFormatter: customCurrencyValueFormatter,
          comparator: currencyComparator,
        });

        periodGroupCol.children.push({
          field: `consfinal-${currentPeriodId}`,
          colId: `consfinal-${currentPeriodId}`,
          headerName: 'Grand Total',
          hide: !isImportantPeriod,
          type: ColumnTypes.numeric,
          valueGetter: consolidationFinalTrialBalanceGetter(
            subsidiaries,
            currentPeriodId
          ),
          valueFormatter: customCurrencyValueFormatter,
          comparator: currencyComparator,
        });
      });

      addConsolidationDifferenceCols(
        settings,
        useGroupsPrecision,
        subsidiaries,
        periods,
        cols
      );
    }

    return cols;
  }
);

const addConsolidationDifferenceCols = (
  settings,
  useGroupsPrecision,
  subsidiaries,
  periods,
  cols
) => {
  const customCurrencyValueFormatter = currencyValueFormatter(
    settings,
    useGroupsPrecision
  );
  periods.forEach((period, periodIndex) => {
    if (periodIndex > 0) {
      const period1 = periods[periodIndex - 1];
      const period2 = period;

      // 3 * periodIndex -> for every period (excluding first period), one cons var column is added
      cols.splice(3 * periodIndex, 0, {
        groupId: `consdifference-${period1.id}-${period2.id}`,
        headerName: `Consolidated Difference (${period1.name} - ${period2.name})`,
        marryChildren: true,
        children: [
          {
            field: `cons-diff-abs-${period1.id}-${period2.id}`,
            colId: `cons-diff-abs-${period1.id}-${period2.id}`,
            headerName: 'Consolidated Diff (abs.)',
            type: ColumnTypes.numeric,
            valueGetter: calculateConsDiffVar(
              period1.id,
              period2.id,
              subsidiaries
            ),
            valueFormatter: customCurrencyValueFormatter,
            comparator: currencyComparator,
          },
          {
            field: `cons-diff-rel-${period1.id}-${period2.id}`,
            colId: `cons-diff-rel-${period1.id}-${period2.id}`,
            headerName: 'Consolidated Diff (%)',
            type: ColumnTypes.numeric,
            valueGetter: calculateConsDiffRel(
              period1.id,
              period2.id,
              subsidiaries
            ),
            valueFormatter: customCurrencyValueFormatter,
            comparator: currencyComparator,
          },
        ],
      });
    }
  });
};

const calculateConsDiffVar = (periodId1, periodId2, subsidiaries) => (
  params
) => {
  const grandTotalForPeriod1Method = grandTotalGetter(subsidiaries, periodId1);
  const grandTotalForPeriod1 = grandTotalForPeriod1Method(params);

  const consFinalTrialBalanceForPeriod2Method = consolidationFinalTrialBalanceGetter(
    subsidiaries,
    periodId2
  );
  const consFinalTrialBalanceForPeriod2 = consFinalTrialBalanceForPeriod2Method(
    params
  );

  return currency(grandTotalForPeriod1).subtract(
    consFinalTrialBalanceForPeriod2
  );
};

const calculateConsDiffRel = (periodId1, periodId2, subsidiaries) => (
  params
) => {
  const consFinalTrialBalanceForPeriod2Method = consolidationFinalTrialBalanceGetter(
    subsidiaries,
    periodId2
  );
  const consFinalTrialBalanceForPeriod2 = consFinalTrialBalanceForPeriod2Method(
    params
  );

  if (!consFinalTrialBalanceForPeriod2.value) {
    // to avoid dividing by 0
    return currency(0);
  }

  const calculateConsDiffVarMethod = calculateConsDiffVar(
    periodId1,
    periodId2,
    subsidiaries
  );
  return calculateConsDiffVarMethod(params)
    .divide(consFinalTrialBalanceForPeriod2)
    .multiply(100);
};

const consolidationTrialBalanceGetter = (subsidiaries, periodId) => (
  params
) => {
  const subsTotalGetterMethod = subsidiariesTotalGetter(subsidiaries, periodId);

  const periodSubsTotal = subsTotalGetterMethod(params);
  const periodNet = calculateNet(params.data, periodId);

  return periodSubsTotal.add(periodNet);
};

const consolidationJournalEntriesGetter = (periodId) => (params) => {
  return currency(params.data[`cje-${periodId}`]); // CJEs
};

const consolidationFinalTrialBalanceGetter = (subsidiaries, periodId) => (
  params
) => {
  const consTrialBalanceMethod = consolidationTrialBalanceGetter(
    subsidiaries,
    periodId
  );
  const consJournalEntriesMethod = consolidationJournalEntriesGetter(periodId);

  return consTrialBalanceMethod(params).add(consJournalEntriesMethod(params));
};

const subsidiariesTotalGetter = (subsidiaries, periodId) => (params) =>
  currencySum(
    subsidiaries.map(
      (subsidiary) => params.data[`sub-${subsidiary.id}-${periodId}`]
    )
  );

const grandTotalGetter = (subsidiaries, periodId) => (params) => {
  const subsTotalGetterMethod = subsidiariesTotalGetter(subsidiaries, periodId);
  return subsTotalGetterMethod(params)
    .add(params.data[`unadjusted-${periodId}`])
    .add(params.data[`adjusted-${periodId}`])
    .add(params.data[`cje-${periodId}`]);
};

const getPeriodColumnsForKpiGrid = (
  periodsById,
  periodColumns,
  settings,
  removeValueGetters = true
) => {
  const allowedGroupColumns = ['period'];
  const allowedChildColumns = [
    'unadjusted',
    'net',
    'sub',
    'subs',
    'grand',
    'constrial',
    'consfinal',
  ];
  const fieldsToOmit = ['aggFunc', 'valueParser', 'valueFormatter'];

  if (removeValueGetters) {
    fieldsToOmit.push('valueGetter');
  }

  const fieldsToAdd = {
    valueFormatter: kpiValueFormatter(settings),
  };

  const fieldsToHide = ['unadjusted'];

  const kpiGridPeriodColumns = [];

  periodColumns.forEach((periodColumn) => {
    const kpiGridPeriodColumn = {
      ...omit(periodColumn, fieldsToOmit),
      ...fieldsToAdd,
      children: [],
    };

    const numberOfDays = periodsById[kpiGridPeriodColumn.groupId]?.numberOfDays;

    allowedGroupColumns.forEach((allowedGroupColumn) => {
      if (periodColumn.groupId.startsWith(allowedGroupColumn)) {
        const hasChildren =
          periodColumn.children && periodColumn.children.length;
        const children = hasChildren ? periodColumn.children : [];

        children.forEach((child) => {
          const [field] = child.field.split('-');
          if (allowedChildColumns.includes(field)) {
            kpiGridPeriodColumn.children.push({
              ...omit(child, fieldsToOmit),
              ...fieldsToAdd,
              numberOfDays,
              hide: fieldsToHide.includes(field),
            });
          }
        });

        kpiGridPeriodColumns.push(kpiGridPeriodColumn);
      }
    });
  });

  return kpiGridPeriodColumns;
};

// Kpi Grid does not need valueGetters
export const periodColumnsForKpiGridSelector = createSelector(
  periodsByIdSelector,
  (state, props) => periodColumnsSelector(state, props),
  settingsSelector,
  getPeriodColumnsForKpiGrid
);

// Kpi Calculations needs valueGetters
export const periodColumnsForKpiCalculationsSelector = createSelector(
  periodsByIdSelector,
  (state, props) => periodColumnsSelector(state, props),
  settingsSelector,
  (periodsById, periodColumns, settings) =>
    getPeriodColumnsForKpiGrid(periodsById, periodColumns, settings, false)
);
